/* ──────────────────────────────────────────────────────────────
   mockData.ts · SK Catalog mock data (demo tenant)
   A cafe/retail chain — shows menu items, retail products with
   variants, services, bundles, multi-location & multi-language.
   Exposed as export const SK
   ────────────────────────────────────────────────────────────── */

// ── Types ──

export interface Tenant {
  id: string;
  name: string;
  kind: string;
  default_locale: string;
}

export interface Location {
  id: string;
  name: string;
}

export interface Language {
  code: string;
  label: string;
  short: string;
  isDefault?: boolean;
}

export interface FieldDef {
  key: string;
  type: string;
  label: string;
  required?: boolean;
  localized?: boolean;
  location_scoped?: boolean;
  options?: string[];
  unit?: string;
}

export interface ItemType {
  id: string;
  name: string;
  emoji: string;
  icon: string;
  description: string;
  fields: FieldDef[];
}

export interface CategoryLeaf {
  id: string;
  name: string;
  count: number;
}

export interface CategoryNode {
  id: string;
  name: string;
  children: CategoryLeaf[];
}

export interface Collection {
  id: string;
  name: string;
  count: number;
  type: string;
  cover: string;
  later?: boolean;
}

export interface Tag {
  id: string;
  name: string;
  count: number;
  ai: boolean;
}

export type LocalizedString = {
  "en-IN": string;
  "ta-IN"?: string;
  "hi-IN"?: string;
};

export interface MediaItem {
  id: string;
  emoji: string;
  status: "READY" | "FAILED" | "PENDING" | "PROCESSING";
  alt: string;
}

export interface AISuggestion {
  id: string;
  field: string;
  target?: string;
  kind: string;
  text: string;
  conf: number;
}

export interface Variant {
  id: string;
  attrs: Record<string, string>;
  price: number | null;
  stock: number;
  sku: string;
}

export interface VariantAxis {
  name: string;
  values: string[];
}

export type ItemState = "PUBLISHED" | "DRAFT" | "SCHEDULED" | "DEACTIVATED";

export interface ItemFields {
  name: LocalizedString;
  description?: LocalizedString;
  base_price: number;
  category?: string;
  spice_level?: string;
  calories?: number;
  is_veg?: boolean;
  dietary?: string[];
  weight?: number;
  stock?: number;
  duration?: string;
  headcount?: number;
}

export interface Item {
  id: string;
  ref: string;
  type: string;
  emoji: string;
  state: ItemState;
  needs_attention: boolean;
  attention_reason?: string;
  updated: string;
  version: number;
  publish_at?: string;
  fields: ItemFields;
  locationPrices: Record<string, number>;
  stockByLoc?: Record<string, number>;
  media: MediaItem[];
  tags: string[];
  collections: string[];
  ai: AISuggestion[];
  variants?: Variant[];
  variantAxes?: VariantAxis[];
}

export interface BundleComponent {
  itemId: string;
  qty: number;
}

export interface BundleOverride {
  pinnedPrice: number;
}

export type BundleState = "PUBLISHED" | "DRAFT" | "DEACTIVATED";
export type BundleModel = "BUNDLE_DYNAMIC" | "BUNDLE_STATIC";

export interface Bundle {
  id: string;
  ref: string;
  emoji: string;
  name: string;
  state: BundleState;
  model: BundleModel;
  needs_attention: boolean;
  attention_reason?: string;
  updated: string;
  staticPrice?: number;
  override?: BundleOverride;
  components: BundleComponent[];
}

export interface PriceReviewQueueEntry {
  bundleId: string;
  trigger: string;
  source: string;
  newRollup: number;
  when: string;
}

export interface ApiKey {
  id: string;
  name: string;
  scope: string;
  prefix: string;
  masked: string;
  created: string;
  lastUsed: string;
  calls: string;
}

export interface Webhook {
  id: string;
  target: string;
  url: string;
  events: string[];
  status: "active" | "paused";
  lastDelivery: string;
}

export interface Feed {
  id: string;
  name: string;
  format: string;
  url: string;
  items: number;
  refresh: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "active" | "invited";
}

export interface Role {
  name: string;
  desc: string;
  perms: string[];
}

// A template's default field — carries a sensible backend field type and
// required flag so the wizard starts correct (Price→CURRENCY, Year→NUMBER…).
export interface TemplateField {
  label: string;
  type: string;        // backend FieldType: TEXT | RICH_TEXT | NUMBER | CURRENCY | BOOLEAN | ENUM | MULTI_SELECT | DATE | MEDIA …
  required?: boolean;
}

export interface Template {
  id: string;
  emoji: string;
  name: string;
  industry: string;
  fields: TemplateField[];   // empty for the Blank template — build from scratch
}

export interface SearchMiss {
  q: string;
  count: number;
}

// ── Helper to make localized object ──
const L = (en: string, ta?: string, hi?: string): LocalizedString => ({
  "en-IN": en,
  ...(ta ? { "ta-IN": ta } : {}),
  ...(hi ? { "hi-IN": hi } : {}),
});

// ── Data ──

const tenants: Tenant[] = [
  { id: "t1", name: "Madras Filter Co.", kind: "Cafe & retail chain", default_locale: "en-IN" },
  { id: "t2", name: "Anand Estates", kind: "Real estate listings", default_locale: "en-IN" },
  { id: "t3", name: "Velocity Auto Parts", kind: "B2B parts distributor", default_locale: "en-IN" },
];

const locations: Location[] = [
  { id: "all", name: "All locations" },
  { id: "mp", name: "Madipakkam" },
  { id: "tn", name: "T. Nagar" },
  { id: "vl", name: "Velachery" },
  { id: "on", name: "Online store" },
];

const languages: Language[] = [
  { code: "en-IN", label: "English (India)", short: "EN", isDefault: true },
  { code: "ta-IN", label: "Tamil", short: "TA" },
  { code: "hi-IN", label: "Hindi", short: "HI" },
];

// Field types are owned by the backend (GET /field-types) and read via
// useFieldTypes(). No field-type data is defined here.

// ── Item types + field definitions ──
const itemTypes: ItemType[] = [
  {
    id: "ty_menu", name: "Menu Item", emoji: "🍽️", icon: "store",
    description: "Food & drinks served in-store and on delivery apps.",
    fields: [
      { key: "name", type: "TEXT", label: "Name", required: true, localized: true },
      { key: "description", type: "RICH_TEXT", label: "Description", localized: true },
      { key: "base_price", type: "CURRENCY", label: "Price", required: true, location_scoped: true },
      { key: "category", type: "ENUM", label: "Category", options: ["Coffee", "Tea", "Breakfast", "Mains", "Desserts"] },
      { key: "spice_level", type: "ENUM", label: "Spice level", options: ["None", "Mild", "Medium", "Hot"] },
      { key: "calories", type: "NUMBER", label: "Calories", unit: "kcal" },
      { key: "is_veg", type: "BOOLEAN", label: "Vegetarian" },
      { key: "dietary", type: "MULTI_SELECT", label: "Dietary tags", options: ["Vegan", "Gluten-free", "Jain", "Contains nuts"] },
      { key: "photo", type: "MEDIA", label: "Photo" },
    ],
  },
  {
    id: "ty_retail", name: "Retail Product", emoji: "📦", icon: "package",
    description: "Packaged goods sold in-store and shipped online. Supports variants.",
    fields: [
      { key: "name", type: "TEXT", label: "Name", required: true, localized: true },
      { key: "description", type: "RICH_TEXT", label: "Description", localized: true },
      { key: "base_price", type: "CURRENCY", label: "Price", required: true, location_scoped: true },
      { key: "category", type: "ENUM", label: "Category", options: ["Beans", "Equipment", "Merchandise"] },
      { key: "stock", type: "NUMBER", label: "Stock level", unit: "units", location_scoped: true },
      { key: "weight", type: "NUMBER", label: "Net weight", unit: "g" },
      { key: "photo", type: "MEDIA", label: "Photo" },
    ],
  },
  {
    id: "ty_service", name: "Service", emoji: "🛎️", icon: "bell",
    description: "Catering, events and bookable services. Quote-on-enquiry pricing.",
    fields: [
      { key: "name", type: "TEXT", label: "Name", required: true, localized: true },
      { key: "description", type: "RICH_TEXT", label: "Description", localized: true },
      { key: "base_price", type: "CURRENCY", label: "From price", location_scoped: true },
      { key: "duration", type: "TEXT", label: "Duration" },
      { key: "headcount", type: "NUMBER", label: "Serves up to", unit: "guests" },
    ],
  },
];

// ── Categories (tree) ──
const categories: CategoryNode[] = [
  { id: "c_bev", name: "Beverages", children: [
    { id: "c_cof", name: "Coffee", count: 8 },
    { id: "c_tea", name: "Tea", count: 4 },
  ]},
  { id: "c_food", name: "Food", children: [
    { id: "c_brk", name: "Breakfast", count: 6 },
    { id: "c_main", name: "Mains", count: 9 },
    { id: "c_des", name: "Desserts", count: 5 },
  ]},
  { id: "c_ret", name: "Retail", children: [
    { id: "c_beans", name: "Beans", count: 7 },
    { id: "c_mer", name: "Merchandise", count: 3 },
  ]},
];

const collections: Collection[] = [
  { id: "col1", name: "Festive Specials", count: 8, type: "Curated", cover: "🪔" },
  { id: "col2", name: "Bestsellers", count: 12, type: "Curated", cover: "⭐" },
  { id: "col3", name: "New this season", count: 5, type: "Curated", cover: "🌿" },
  { id: "col4", name: "Under ₹200", count: 0, type: "Dynamic", cover: "💸", later: true },
];

const tags: Tag[] = [
  { id: "tg1", name: "bestseller", count: 12, ai: false },
  { id: "tg2", name: "signature", count: 6, ai: false },
  { id: "tg3", name: "seasonal", count: 9, ai: true },
  { id: "tg4", name: "spicy", count: 4, ai: true },
  { id: "tg5", name: "vegan", count: 7, ai: true },
  { id: "tg6", name: "gift-worthy", count: 3, ai: true },
];

// ── Items ──
const items: Item[] = [
  {
    id: "it1", ref: "MENU-FILTER-COF", type: "ty_menu", emoji: "☕",
    state: "PUBLISHED", needs_attention: false, updated: "2h ago", version: 14,
    fields: {
      name: L("Madras Filter Coffee", "மெட்ராஸ் பில்டர் காபி", "मद्रास फ़िल्टर कॉफ़ी"),
      description: L("Slow-brewed decoction with frothy boiled milk, served in a traditional steel tumbler.", "", "धीमी गति से बनी डिकॉक्शन।"),
      base_price: 60, category: "Coffee", spice_level: "None", calories: 90, is_veg: true, dietary: ["Vegan"],
    },
    locationPrices: { mp: 60, tn: 70, vl: 60, on: 75 },
    stockByLoc: {},
    media: [
      { id: "m1", emoji: "☕", status: "READY", alt: "Filter coffee in steel tumbler" },
      { id: "m2", emoji: "🥛", status: "READY", alt: "Frothy boiled milk" },
      { id: "m1f", emoji: "📷", status: "FAILED", alt: "" },
    ],
    tags: ["bestseller", "signature", "vegan"], collections: ["col2"],
    ai: [
      { id: "ai1", field: "description", target: "ta-IN", kind: "translation", text: "நுரை பாலுடன் பாரம்பரிய எஃகு டம்ளரில் வழங்கப்படும் மெதுவாக காய்ச்சிய காபி.", conf: 0.91 },
      { id: "ai2", field: "tags", kind: "tag", text: "south-indian", conf: 0.84 },
    ],
  },
  {
    id: "it2", ref: "MENU-MASALA-DOSA", type: "ty_menu", emoji: "🥞",
    state: "PUBLISHED", needs_attention: false, updated: "5h ago", version: 8,
    fields: {
      name: L("Masala Dosa", "மசாலா தோசை"),
      description: L("Crisp rice & lentil crepe with spiced potato filling, sambar and chutney trio."),
      base_price: 120, category: "Breakfast", spice_level: "Medium", calories: 387, is_veg: true, dietary: ["Vegan", "Gluten-free"],
    },
    locationPrices: { mp: 120, tn: 140, vl: 120, on: 150 },
    media: [{ id: "m3", emoji: "🥞", status: "READY", alt: "Golden masala dosa" }],
    tags: ["bestseller", "signature"], collections: ["col2"],
    ai: [{ id: "ai3", field: "category", kind: "category", text: "Breakfast", conf: 0.96 }],
  },
  {
    id: "it3", ref: "MENU-CHAI", type: "ty_menu", emoji: "🍵",
    state: "PUBLISHED", needs_attention: false, updated: "1d ago", version: 5,
    fields: {
      name: L("Masala Chai", "மசாலா டீ"),
      description: L("Black tea simmered with ginger, cardamom and fresh milk."),
      base_price: 40, category: "Tea", spice_level: "Mild", calories: 110, is_veg: true, dietary: [],
    },
    locationPrices: { mp: 40, tn: 50, vl: 40, on: 55 },
    media: [{ id: "m4", emoji: "🍵", status: "READY", alt: "Masala chai" }],
    tags: ["bestseller"], collections: [],
    ai: [],
  },
  {
    id: "it4", ref: "MENU-PANEER-BTR", type: "ty_menu", emoji: "🍛",
    state: "DRAFT", needs_attention: false, updated: "30m ago", version: 2,
    fields: {
      name: L("Paneer Butter Masala"),
      description: L(""),
      base_price: 240, category: "Mains", spice_level: "Medium", calories: 540, is_veg: true, dietary: ["Contains nuts"],
    },
    locationPrices: { mp: 240 },
    media: [],
    tags: [], collections: [],
    ai: [
      { id: "ai4", field: "description", kind: "description", text: "Cottage cheese cubes simmered in a velvety tomato-cashew gravy, finished with cream and crushed fenugreek.", conf: 0.88 },
      { id: "ai5", field: "tags", kind: "tag", text: "rich", conf: 0.72 },
      { id: "ai6", field: "tags", kind: "tag", text: "popular", conf: 0.65 },
    ],
  },
  {
    id: "it5", ref: "MENU-FILTER-CB", type: "ty_menu", emoji: "🍮",
    state: "SCHEDULED", needs_attention: false, updated: "3h ago", version: 3,
    publish_at: "Tomorrow 09:00",
    fields: {
      name: L("Filter Coffee Crème Brûlée"),
      description: L("Limited festive dessert — caramelised custard infused with our house decoction."),
      base_price: 180, category: "Desserts", spice_level: "None", calories: 320, is_veg: true, dietary: [],
    },
    locationPrices: { tn: 180 },
    media: [{ id: "m5", emoji: "🍮", status: "READY", alt: "Crème brûlée" }],
    tags: ["seasonal"], collections: ["col1", "col3"],
    ai: [],
  },
  {
    id: "it6", ref: "RET-BEANS-PEABERRY", type: "ty_retail", emoji: "🫘",
    state: "PUBLISHED", needs_attention: false, updated: "2d ago", version: 21,
    fields: {
      name: L("Peaberry Coffee Beans"),
      description: L("Single-origin Chikmagalur peaberry, medium-dark roast. Whole bean."),
      base_price: 450, category: "Beans", weight: 250,
    },
    locationPrices: { mp: 450, tn: 450, vl: 450, on: 480 },
    stockByLoc: { mp: 24, tn: 12, vl: 0, on: 140 },
    media: [{ id: "m6", emoji: "🫘", status: "READY", alt: "Coffee beans bag" }],
    tags: ["bestseller", "gift-worthy"], collections: ["col2"],
    variants: [
      { id: "v1", attrs: { Size: "250g", Roast: "Medium-dark" }, price: 450, stock: 140, sku: "PB-250-MD" },
      { id: "v2", attrs: { Size: "500g", Roast: "Medium-dark" }, price: 820, stock: 64, sku: "PB-500-MD" },
      { id: "v3", attrs: { Size: "1kg", Roast: "Medium-dark" }, price: 1500, stock: 18, sku: "PB-1K-MD" },
      { id: "v4", attrs: { Size: "250g", Roast: "Dark" }, price: null, stock: 40, sku: "PB-250-DK" },
      { id: "v5", attrs: { Size: "500g", Roast: "Dark" }, price: null, stock: 22, sku: "PB-500-DK" },
    ],
    variantAxes: [
      { name: "Size", values: ["250g", "500g", "1kg"] },
      { name: "Roast", values: ["Medium-dark", "Dark"] },
    ],
    ai: [],
  },
  {
    id: "it7", ref: "RET-MUG-STEEL", type: "ty_retail", emoji: "🥤",
    state: "PUBLISHED", needs_attention: true, attention_reason: "Low stock at 2 locations",
    updated: "4h ago", version: 9,
    fields: {
      name: L("Traditional Steel Tumbler Set"),
      description: L("Davara-tumbler set, the authentic way to drink filter coffee. Set of 2."),
      base_price: 320, category: "Merchandise", weight: 280,
    },
    locationPrices: { mp: 320, tn: 320, vl: 320, on: 350 },
    stockByLoc: { mp: 3, tn: 2, vl: 30, on: 88 },
    media: [{ id: "m7", emoji: "🥤", status: "READY", alt: "Steel tumbler set" }],
    tags: ["gift-worthy"], collections: ["col1"],
    ai: [],
  },
  {
    id: "it8", ref: "SVC-CATERING", type: "ty_service", emoji: "🍱",
    state: "PUBLISHED", needs_attention: false, updated: "1w ago", version: 4,
    fields: {
      name: L("Office Breakfast Catering"),
      description: L("South Indian breakfast spread delivered to your office. Quote on enquiry."),
      base_price: 180, duration: "Per head", headcount: 200,
    },
    locationPrices: { tn: 180 },
    media: [{ id: "m8", emoji: "🍱", status: "READY", alt: "Catering spread" }],
    tags: [], collections: [],
    ai: [],
  },
  {
    id: "it9", ref: "MENU-VADA", type: "ty_menu", emoji: "🍩",
    state: "DEACTIVATED", needs_attention: false, updated: "3w ago", version: 12,
    fields: {
      name: L("Medu Vada (2 pc)"),
      description: L("Crisp lentil doughnuts. Temporarily off the menu — supplier change."),
      base_price: 70, category: "Breakfast", spice_level: "Mild", calories: 280, is_veg: true, dietary: ["Vegan"],
    },
    locationPrices: { mp: 70, tn: 80 },
    media: [{ id: "m9", emoji: "🍩", status: "READY", alt: "Medu vada" }],
    tags: [], collections: [],
    ai: [],
  },
  {
    id: "it10", ref: "RET-FILTER-KIT", type: "ty_retail", emoji: "⚗️",
    state: "DRAFT", needs_attention: false, updated: "1h ago", version: 1,
    fields: {
      name: L("Home Filter Coffee Kit"),
      description: L(""),
      base_price: 1200, category: "Equipment", weight: 600,
    },
    locationPrices: { on: 1200 },
    stockByLoc: { on: 35 },
    media: [
      { id: "m10a", emoji: "⚗️", status: "READY", alt: "Filter coffee kit" },
      { id: "m10b", emoji: "🖼️", status: "PENDING", alt: "" },
      { id: "m10c", emoji: "🥄", status: "PROCESSING", alt: "" },
      { id: "m10", emoji: "📷", status: "FAILED", alt: "" },
    ],
    tags: [], collections: [],
    ai: [{ id: "ai7", field: "description", kind: "description", text: "Everything to brew authentic filter coffee at home: a stainless steel decoction filter, 250g of fresh grounds, and a davara-tumbler set.", conf: 0.9 }],
  },
  {
    id: "it11", ref: "MENU-IDLI", type: "ty_menu", emoji: "🍚",
    state: "PUBLISHED", needs_attention: false, updated: "2d ago", version: 7,
    fields: {
      name: L("Idli (3 pc)"),
      description: L("Steamed rice cakes with sambar and chutney."),
      base_price: 80, category: "Breakfast", spice_level: "Mild", calories: 220, is_veg: true, dietary: ["Vegan", "Gluten-free"],
    },
    locationPrices: { mp: 80, tn: 90, vl: 80, on: 95 },
    media: [{ id: "m11", emoji: "🍚", status: "READY", alt: "Idli plate" }],
    tags: ["bestseller", "vegan"], collections: ["col2"],
    ai: [],
  },
  {
    id: "it12", ref: "MENU-COLD-COF", type: "ty_menu", emoji: "🧋",
    state: "PUBLISHED", needs_attention: false, updated: "6h ago", version: 6,
    fields: {
      name: L("Cold Filter Coffee"),
      description: L("Our signature decoction over ice with lightly sweetened milk."),
      base_price: 110, category: "Coffee", spice_level: "None", calories: 150, is_veg: true, dietary: [],
    },
    locationPrices: { mp: 110, tn: 120, vl: 110, on: 130 },
    media: [{ id: "m12", emoji: "🧋", status: "READY", alt: "Cold coffee" }],
    tags: ["seasonal"], collections: ["col3"],
    ai: [{ id: "ai8", field: "tags", kind: "tag", text: "summer", conf: 0.79 }],
  },
];

// ── Bundles (item_relations) ──
const bundles: Bundle[] = [
  {
    id: "bn1", ref: "BND-BREAKFAST", emoji: "🍳", name: "Madras Breakfast Combo",
    state: "PUBLISHED", model: "BUNDLE_DYNAMIC", needs_attention: false, updated: "1d ago",
    components: [
      { itemId: "it2", qty: 1 },
      { itemId: "it1", qty: 1 },
      { itemId: "it11", qty: 1 },
    ],
  },
  {
    id: "bn2", ref: "BND-FESTIVE", emoji: "🎁", name: "Festive Gift Hamper",
    state: "PUBLISHED", model: "BUNDLE_STATIC", staticPrice: 999, needs_attention: true,
    attention_reason: "A component was deactivated", updated: "2h ago",
    components: [
      { itemId: "it6", qty: 1 },
      { itemId: "it7", qty: 1 },
      { itemId: "it9", qty: 2 }, // Medu Vada is DEACTIVATED → triggers attention
    ],
  },
  {
    id: "bn3", ref: "BND-5PACK", emoji: "📦", name: "5-Pack Value Box",
    state: "PUBLISHED", model: "BUNDLE_DYNAMIC", needs_attention: false, updated: "3d ago",
    // Multi-pack math: 5 × Peaberry beans (it6) — rollup follows the component.
    components: [
      { itemId: "it6", qty: 5 },
    ],
  },
  {
    id: "bn4", ref: "BND-PREMIUM-GIFT", emoji: "🎀", name: "Premium Gift Set",
    state: "PUBLISHED", model: "BUNDLE_DYNAMIC", needs_attention: false, updated: "1w ago",
    // PRICE_OVERRIDDEN: a human pinned this price off the formula. Drift is FYI, not a nag.
    override: { pinnedPrice: 700 },
    components: [
      { itemId: "it6", qty: 1 },
      { itemId: "it7", qty: 1 },
    ],
  },
];

// ── Bundle price-review queue ──
// Dynamic bundles flagged (not auto-updated) because a component price changed via a
// non-interactive path — import, API, location edit, or scheduled promo. Each row carries
// a freshly computed rollup; the admin batch-reviews them in the resolution queue.
const priceReviewQueue: PriceReviewQueueEntry[] = [
  { bundleId: "bn3", trigger: "Import · supplier price list", source: "import", newRollup: 2400, when: "2h ago" },
  { bundleId: "bn1", trigger: "API · price update on Filter Coffee", source: "api", newRollup: 285, when: "5h ago" },
];

// ── Distribution ──
const apiKeys: ApiKey[] = [
  { id: "k1", name: "Website (read-only)", scope: "read", prefix: "sk_live_pub_", masked: "7Hq2…a9F0", created: "Mar 2026", lastUsed: "2 min ago", calls: "1.2M" },
  { id: "k2", name: "SK Chat agent (MCP)", scope: "read", prefix: "sk_live_mcp_", masked: "Kp9m…3xQ1", created: "Feb 2026", lastUsed: "just now", calls: "840K" },
  { id: "k3", name: "ERP sync (read+write)", scope: "read+write", prefix: "sk_live_srv_", masked: "Lz4n…8vR2", created: "Jan 2026", lastUsed: "1h ago", calls: "96K" },
];

const webhooks: Webhook[] = [
  { id: "w1", target: "Swiggy connector", url: "https://hooks.swiggy.partner/sk/menu", events: ["item.published", "price.changed", "item.deactivated"], status: "active", lastDelivery: "OK · 2m ago" },
  { id: "w2", target: "Meta Catalog", url: "https://graph.facebook.com/v19/catalog", events: ["item.published", "item.updated"], status: "active", lastDelivery: "OK · 14m ago" },
  { id: "w3", target: "Zomato (staging)", url: "https://hooks.zomato.dev/sk/feed", events: ["item.published"], status: "paused", lastDelivery: "Paused" },
];

const feeds: Feed[] = [
  { id: "f1", name: "Google Merchant XML", format: "XML", url: "/feeds/google.xml", items: 41, refresh: "Hourly" },
  { id: "f2", name: "Full catalog export", format: "CSV", url: "/feeds/catalog.csv", items: 53, refresh: "On demand" },
  { id: "f3", name: "Sitemap", format: "XML", url: "/sitemap.xml", items: 41, refresh: "Daily" },
];

// ── Roles & users ──
const users: User[] = [
  { id: "u1", name: "Priya Raman", email: "priya@madrasfilter.co", role: "Admin", status: "active" },
  { id: "u2", name: "Karthik S", email: "karthik@madrasfilter.co", role: "Editor", status: "active" },
  { id: "u3", name: "Divya Menon", email: "divya@madrasfilter.co", role: "Editor", status: "active" },
  { id: "u4", name: "Arjun Pillai", email: "arjun@madrasfilter.co", role: "Viewer", status: "invited" },
];

const roles: Role[] = [
  { name: "Admin", desc: "Full control — schema, distribution, settings, publishing.", perms: ["Configure item types & fields", "Manage distribution & API keys", "Manage users & roles", "Publish & deactivate", "Edit all content"] },
  { name: "Editor", desc: "Day-to-day content. Cannot change schema or distribution.", perms: ["Add & edit items", "Manage media & bundles", "Respond to AI suggestions", "Publish & deactivate"] },
  { name: "Viewer", desc: "Read-only access for reporting and reference.", perms: ["View all items", "View reports", "Export data"] },
];

// ── Industry starter templates ──
// Field types are sensible defaults; everything stays editable in the wizard.
const templates: Template[] = [
  {
    id: "tpl_menu", emoji: "🍽️", name: "Menu Item", industry: "Restaurants & cafes",
    fields: [
      { label: "Name", type: "TEXT", required: true },
      { label: "Description", type: "RICH_TEXT" },
      { label: "Price", type: "CURRENCY", required: true },
      { label: "Category", type: "ENUM" },
      { label: "Spice level", type: "ENUM" },
      { label: "Calories", type: "NUMBER" },
      { label: "Vegetarian", type: "BOOLEAN" },
      { label: "Photo", type: "MEDIA" },
    ],
  },
  {
    id: "tpl_saas", emoji: "💻", name: "SaaS Plan", industry: "Software",
    fields: [
      { label: "Plan name", type: "TEXT", required: true },
      { label: "Description", type: "RICH_TEXT" },
      { label: "Monthly price", type: "CURRENCY", required: true },
      { label: "Annual price", type: "CURRENCY" },
      { label: "Seats", type: "NUMBER" },
      { label: "Features", type: "MULTI_SELECT" },
      { label: "Trial days", type: "NUMBER" },
    ],
  },
  {
    id: "tpl_auto", emoji: "🚗", name: "Auto Listing", industry: "Automotive",
    fields: [
      { label: "Make", type: "TEXT", required: true },
      { label: "Model", type: "TEXT", required: true },
      { label: "Year", type: "NUMBER", required: true },
      { label: "Price", type: "CURRENCY", required: true },
      { label: "Mileage", type: "NUMBER" },
      { label: "Fuel type", type: "ENUM" },
      { label: "Transmission", type: "ENUM" },
      { label: "Location", type: "TEXT" },
      { label: "Photos", type: "MEDIA" },
    ],
  },
  {
    id: "tpl_clinic", emoji: "🩺", name: "Clinic Service", industry: "Healthcare",
    fields: [
      { label: "Service name", type: "TEXT", required: true },
      { label: "Description", type: "RICH_TEXT" },
      { label: "Price", type: "CURRENCY" },
      { label: "Duration", type: "TEXT" },
      { label: "Practitioner", type: "TEXT" },
      { label: "Prep notes", type: "TEXT" },
    ],
  },
  {
    id: "tpl_estate", emoji: "🏠", name: "Property Listing", industry: "Real estate",
    fields: [
      { label: "Title", type: "TEXT", required: true },
      { label: "Description", type: "RICH_TEXT" },
      { label: "Price", type: "CURRENCY", required: true },
      { label: "Bedrooms", type: "NUMBER" },
      { label: "Area", type: "NUMBER" },
      { label: "Location", type: "TEXT" },
      { label: "Amenities", type: "MULTI_SELECT" },
      { label: "Photos", type: "MEDIA" },
    ],
  },
  {
    id: "tpl_blank", emoji: "✨", name: "Blank type", industry: "Start from scratch",
    fields: [],
  },
];

// dashboard search misses
const searchMisses: SearchMiss[] = [
  { q: "oat milk latte", count: 34 },
  { q: "gluten free cake", count: 21 },
  { q: "cold brew concentrate", count: 18 },
  { q: "decaf", count: 12 },
  { q: "vegan brownie", count: 9 },
];

// ── Helper functions ──

export function itemById(id: string): Item | undefined {
  return (items.find(i => i.id === id) || bundles.find(b => b.id === id)) as Item | undefined;
}

export function typeById(id: string): ItemType | undefined {
  return itemTypes.find(t => t.id === id);
}

export function itemName(it: any, locale: string = "en-IN"): string {
  const n = it.fields?.name;
  if (!n) return (it as { name?: string }).name || "(untitled)";
  return n[locale as keyof LocalizedString] || n["en-IN"] || Object.values(n)[0] || "(untitled)";
}

// Roll a bundle's price up from its components. Pass a { itemId: price } map to
// preview the effect of a (proposed) component price change.
export function bundleRollup(bundle: Bundle, priceMap: Record<string, number> = {}): number {
  return (bundle.components || []).reduce((sum, c) => {
    const it = itemById(c.itemId) as Item | undefined;
    const unit = priceMap[c.itemId] != null ? priceMap[c.itemId] : (it?.fields?.base_price || 0);
    return sum + unit * c.qty;
  }, 0);
}

export const SK = {
  tenants,
  locations,
  languages,
  itemTypes,
  categories,
  collections,
  tags,
  items,
  bundles,
  apiKeys,
  webhooks,
  feeds,
  users,
  roles,
  templates,
  searchMisses,
  priceReviewQueue,
  itemById,
  typeById,
  itemName,
  bundleRollup,
};
